import { useState, useEffect } from "react";

export default function AccessibilityButton() {
  const [open, setOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [highContrast, setHighContrast] = useState(false);
  const [dyslexicFont, setDyslexicFont] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [textOnly, setTextOnly] = useState(false);

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}%`;
  }, [fontSize]);

  useEffect(() => {
    if (highContrast) {
      document.documentElement.classList.add("high-contrast");
    } else {
      document.documentElement.classList.remove("high-contrast");
    }
  }, [highContrast]);

  useEffect(() => {
    if (dyslexicFont) {
      document.documentElement.classList.add("dyslexic-font");
    } else {
      document.documentElement.classList.remove("dyslexic-font");
    }
  }, [dyslexicFont]);

  useEffect(() => {
    if (reduceMotion) {
      document.documentElement.classList.add("reduce-motion");
    } else {
      document.documentElement.classList.remove("reduce-motion");
    }
  }, [reduceMotion]);

  useEffect(() => {
    if (textOnly) {
      document.documentElement.classList.add("text-only-mode");
    } else {
      document.documentElement.classList.remove("text-only-mode");
    }
  }, [textOnly]);

  const handleIncrease = () => setFontSize((prev) => Math.min(prev + 10, 140));
  const handleDecrease = () => setFontSize((prev) => Math.max(prev - 10, 80));
  const handleReset = () => {
    setFontSize(100);
    setHighContrast(false);
    setDyslexicFont(false);
    setReduceMotion(false);
    setTextOnly(false);
  };

  return (
    <>
      {/* Style injections */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&display=swap');
        .high-contrast body,
        .high-contrast div,
        .high-contrast section,
        .high-contrast header {
          background-color: #000 !important;
          color: #fff !important;
        }
        .high-contrast p,
        .high-contrast span,
        .high-contrast h1,
        .high-contrast h2,
        .high-contrast h3,
        .high-contrast h4 {
          color: #fff !important;
        }
        .reduce-motion *,
        .reduce-motion *::before,
        .reduce-motion *::after {
          animation: none !important;
          transition: none !important;
        }
        .dyslexic-font,
        .dyslexic-font * {
          font-family: 'Atkinson Hyperlegible', sans-serif !important;
          letter-spacing: 0.05em !important;
          word-spacing: 0.1em !important;
          line-height: 1.7 !important;
        }
        .text-only-mode img,
        .text-only-mode video,
        .text-only-mode iframe,
        .text-only-mode canvas,
        .text-only-mode svg:not(.keep-visible),
        .text-only-mode [style*="background-image"] {
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
        .text-only-mode [class*="bg-"][class*="url"],
        .text-only-mode [style*="background-image"] {
          background-image: none !important;
        }
        @keyframes pulse-access {
          0%, 100% { box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35); }
          50% { box-shadow: 0 4px 20px rgba(99, 102, 241, 0.6); }
        }
      `}</style>

      {/* Accessibility Panel */}
      {open && (
        <div
          className="fixed bottom-44 left-4 z-50 w-52 rounded-3xl border-[3px] border-gray-900 bg-white overflow-hidden"
          style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}
          dir="rtl"
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-gray-900 bg-indigo-50">
            <div className="flex items-center gap-2">
              <i className="ri-user-heart-line text-indigo-600 text-base"></i>
              <span className="text-sm font-black text-gray-900">נגישות</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
              aria-label="סגור"
            >
              <i className="ri-close-line text-gray-600 text-sm"></i>
            </button>
          </div>

          {/* Options */}
          <div className="flex flex-col gap-1 p-3">
            <button
              onClick={handleIncrease}
              className="flex items-center gap-3 w-full rounded-2xl border-2 border-gray-900 px-3 py-2.5 text-sm font-bold text-gray-900 bg-white hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-xl bg-indigo-100 border-2 border-gray-900 text-base font-black">A+</span>
              הגדלת טקסט
            </button>

            <button
              onClick={handleDecrease}
              className="flex items-center gap-3 w-full rounded-2xl border-2 border-gray-900 px-3 py-2.5 text-sm font-bold text-gray-900 bg-white hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-xl bg-indigo-100 border-2 border-gray-900 text-base font-black">A-</span>
              הקטנת טקסט
            </button>

            <button
              onClick={() => setHighContrast((prev) => !prev)}
              className={`flex items-center gap-3 w-full rounded-2xl border-2 border-gray-900 px-3 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                highContrast
                  ? "bg-gray-900 text-white"
                  : "bg-white text-gray-900 hover:bg-gray-50"
              }`}
            >
              <span className={`w-7 h-7 flex items-center justify-center rounded-xl border-2 border-gray-900 ${highContrast ? "bg-white" : "bg-gray-900"}`}>
                <i className={`ri-contrast-2-line text-sm ${highContrast ? "text-gray-900" : "text-white"}`}></i>
              </span>
              ניגודיות גבוהה
            </button>

            <button
              onClick={() => setReduceMotion((prev) => !prev)}
              className={`flex items-center gap-3 w-full rounded-2xl border-2 border-gray-900 px-3 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                reduceMotion
                  ? "bg-green-400 text-gray-900"
                  : "bg-white text-gray-900 hover:bg-gray-50"
              }`}
            >
              <span className={`w-7 h-7 flex items-center justify-center rounded-xl border-2 border-gray-900 ${
                reduceMotion ? "bg-gray-900" : "bg-green-100"
              }`}>
                <i className={`ri-pause-circle-line text-sm ${reduceMotion ? "text-white" : "text-gray-900"}`}></i>
              </span>
              הפחתת אנימציות
            </button>

            <button
              onClick={() => setDyslexicFont((prev) => !prev)}
              className={`flex items-center gap-3 w-full rounded-2xl border-2 border-gray-900 px-3 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                dyslexicFont
                  ? "bg-amber-400 text-gray-900"
                  : "bg-white text-gray-900 hover:bg-gray-50"
              }`}
            >
              <span className={`w-7 h-7 flex items-center justify-center rounded-xl border-2 border-gray-900 text-xs font-black ${
                dyslexicFont ? "bg-gray-900 text-white" : "bg-amber-100 text-gray-900"
              }`}>Aa</span>
              גופן לדיסלקציה
            </button>

            <button
              onClick={() => setTextOnly((prev) => !prev)}
              className={`flex items-center gap-3 w-full rounded-2xl border-2 border-gray-900 px-3 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                textOnly
                  ? "bg-rose-400 text-gray-900"
                  : "bg-white text-gray-900 hover:bg-gray-50"
              }`}
            >
              <span className={`w-7 h-7 flex items-center justify-center rounded-xl border-2 border-gray-900 ${
                textOnly ? "bg-gray-900" : "bg-rose-100"
              }`}>
                <i className={`ri-eye-off-line text-sm ${textOnly ? "text-white" : "text-gray-900"}`}></i>
              </span>
              רק טקסט
            </button>

            <button
              onClick={handleReset}
              className="flex items-center gap-3 w-full rounded-2xl border-2 border-gray-200 px-3 py-2.5 text-sm font-bold text-gray-500 bg-white hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap mt-1"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-xl bg-gray-100 border-2 border-gray-200">
                <i className="ri-refresh-line text-gray-500 text-sm"></i>
              </span>
              איפוס הגדרות
            </button>
          </div>

          {/* Font size indicator */}
          <div className="px-4 pb-3 text-center">
            <span className="text-xs text-gray-400 font-medium">גודל טקסט: {fontSize}%</span>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="fixed bottom-24 left-4 z-50 w-14 h-14 flex items-center justify-center rounded-full border-[3px] border-gray-900 cursor-pointer transition-transform hover:scale-110 whitespace-nowrap"
        style={{
          background: "#6366F1",
          animation: "pulse-access 2.5s infinite",
        }}
        aria-label="נגישות"
      >
        <i className="ri-user-heart-line text-white text-2xl"></i>
      </button>
    </>
  );
}
